import React, { useEffect, useState, useMemo } from "react";
import { Translator } from "./translator";

const InfoLegge = () => {
  const { translateText, lang } = Translator();

  // ===== Fixed IT / EN / FR text =====
  const baseTextByLang = useMemo(
    () => ({
      it: {
        title: "Informazioni ai sensi della legge sui servizi digitali",
        updated: "Ultimo aggiornamento: 2025",

        intro:
          "Ai sensi della Legge sui Servizi Digitali (Digital Services Act - Regolamento (UE) 2022/2065), Te.C.H-e fornisce le seguenti informazioni obbligatorie relative al proprio servizio digitale.",

        section1: "Identità del Fornitore del Servizio",
        section1Content: `Denominazione: Te.C.H-e Technology for Cultural Heritage
Sede legale: Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italia
C.F. e P.IVA: 09885961210
Email di contatto: emozionartisrl@gmail.com`,

        section2: "Descrizione del Servizio",
        section2Content:
          "Te.C.H-e offre una piattaforma digitale e app (TecheTour) per la fruizione del patrimonio culturale materiale e immateriale, con tecnologie innovative immersive, intelligenza artificiale e sistemi NFT.",

        section3: "Supervisione dei Contenuti",
        section3Content:
          "Te.C.H-e adotta sistemi di moderazione e verifica per garantire che i contenuti presenti sulla piattaforma rispettino la normativa vigente, i diritti di proprietà intellettuale e i principi di inclusività, sicurezza e correttezza.",

        section4: "Segnalazioni e Reclami",
        section4Content:
          "Gli utenti possono inviare segnalazioni o reclami relativi a contenuti o servizi tramite l’email indicata o attraverso l’apposita sezione “Contattaci” presente sull’app e sulla piattaforma Te.C.H-e.",

        section5: "Responsabilità",
        section5Content:
          "Te.C.H-e si impegna a rispettare tutte le disposizioni previste dal Digital Services Act e dalle normative italiane ed europee applicabili in materia di servizi digitali.",

        footer: "© {year} Te.C.H-e – Tutti i diritti riservati.",
      },

      en: {
        title: "Information under the Digital Services Act",
        updated: "Last updated: 2025",

        intro:
          "Pursuant to the Digital Services Act (Regulation (EU) 2022/2065), Te.C.H-e provides the following mandatory information regarding its digital service.",

        section1: "Identity of the Service Provider",
        section1Content: `Name: Te.C.H-e Technology for Cultural Heritage
Registered office: Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italy
Tax Code and VAT no.: 09885961210
Contact email: emozionartisrl@gmail.com`,

        section2: "Description of the Service",
        section2Content:
          "Te.C.H-e provides a digital platform and app (TecheTour) for the enjoyment of tangible and intangible cultural heritage, using innovative immersive technologies, artificial intelligence and NFT-based systems.",

        section3: "Content Supervision",
        section3Content:
          "Te.C.H-e applies moderation and verification systems to ensure that the content available on the platform complies with applicable legislation, intellectual property rights, and the principles of inclusiveness, safety and fairness.",

        section4: "Reports and Complaints",
        section4Content:
          'Users may submit reports or complaints regarding content or services via the email provided, or through the dedicated “Contact us” section available in the app and on the Te.C.H-e platform.',

        section5: "Liability",
        section5Content:
          "Te.C.H-e undertakes to comply with all provisions laid down by the Digital Services Act and by applicable Italian and European regulations concerning digital services.",

        footer: "© {year} Te.C.H-e – All rights reserved.",
      },

      fr: {
        title:
          "Informations au titre de la loi sur les services numériques",
        updated: "Dernière mise à jour : 2025",

        intro:
          "Conformément au Digital Services Act (Règlement (UE) 2022/2065), Te.C.H-e fournit les informations obligatoires suivantes concernant son service numérique.",

        section1: "Identité du prestataire de services",
        section1Content: `Dénomination : Te.C.H-e Technology for Cultural Heritage
Siège social : Via Giuseppe Verdi 46, 80038 Pomigliano d'Arco, Italie
Code fiscal et N° de TVA : 09885961210
Adresse e-mail de contact : emozionartisrl@gmail.com`,

        section2: "Description du service",
        section2Content:
          "Te.C.H-e propose une plateforme numérique et une application (TecheTour) dédiées à la valorisation du patrimoine culturel matériel et immatériel, en s’appuyant sur des technologies immersives innovantes, l’intelligence artificielle et des systèmes basés sur les NFT.",

        section3: "Supervision des contenus",
        section3Content:
          "Te.C.H-e met en place des systèmes de modération et de vérification afin de garantir que les contenus présents sur la plateforme respectent la législation en vigueur, les droits de propriété intellectuelle ainsi que les principes d’inclusivité, de sécurité et de loyauté.",

        section4: "Signalements et réclamations",
        section4Content:
          "Les utilisateurs peuvent envoyer des signalements ou des réclamations concernant les contenus ou les services via l’adresse e-mail indiquée ou au moyen de la section « Contactez-nous » disponible sur l’application et la plateforme Te.C.H-e.",

        section5: "Responsabilité",
        section5Content:
          "Te.C.H-e s’engage à respecter l’ensemble des dispositions prévues par le Digital Services Act ainsi que par les réglementations italiennes et européennes applicables en matière de services numériques.",

        footer: "© {year} Te.C.H-e – Tous droits réservés.",
      },
    }),
    []
  );

  const [text, setText] = useState(
    baseTextByLang[lang] || baseTextByLang.en
  );

  // IT/EN/FR fixed; other languages via Translator (from Italian)
  useEffect(() => {
    if (lang === "it" || lang === "en" || lang === "fr") {
      setText(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    let cancelled = false;

    const translateAll = async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setText(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setText(baseTextByLang.en);
        }
      }
    };

    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 font-sans text-gray-800">
      <h1 className="text-black text-3xl font-bold text-center mb-4">
        {text.title}
      </h1>
      <p className="text-sm text-gray-500 text-center mb-6">
        {text.updated}
      </p>
      <p className="mb-6">{text.intro}</p>

      <Section title={text.section1}>
        {text.section1Content.split("\n").map((line, index) => (
          <p key={index} className="mb-1">
            {line}
          </p>
        ))}
      </Section>

      <Section title={text.section2}>
        <p>{text.section2Content}</p>
      </Section>

      <Section title={text.section3}>
        <p>{text.section3Content}</p>
      </Section>

      <Section title={text.section4}>
        <p>{text.section4Content}</p>
      </Section>

      <Section title={text.section5}>
        <p>{text.section5Content}</p>
      </Section>

      <footer className="text-sm text-center text-gray-600 mt-10">
        {text.footer.replace("{year}", new Date().getFullYear())}
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-black text-xl font-semibold mb-2">
      {title}
    </h2>
    <div className="text-base leading-relaxed">{children}</div>
  </section>
);

export default InfoLegge;
